import React, { useEffect } from 'react';
import { SPACES } from 'common/constants/sizes';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { Tooltip } from '@material-ui/core';
import GitIntegration from '../GitIntegration/GitIntegrationWrapper';
import { pluralize } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING, PROVIDERS_MAP } from 'common/constants/constants';
import startCase from 'lodash/startCase';
import orderBy from 'lodash/orderBy';
import { createGitOAuthModalOptions } from 'modals/modal-options-factory';
import withGitIntegrationsModals from './withGitIntegrationsModals';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import useAutoRepoDiscovery from '../GitIntegration/useAutoRepoDiscovery';
import { FilterChip } from '@linearb/linui-common';
import Icon from 'components/base/Icon';
import { LBButton } from '@linearb/buzz';

const Header = ({ gitIntegrationsLength = 0, selectionProps }) => {
  const isSelectedOnlyMode =
    selectionProps?.isSelectedOnly && selectionProps?.setIsSelectedOnly;

  return (
    <Flex
      direction='column'
      justify='space-between'
      content='flex-start'
      items='flex-start'
    >
      <Flex items='center'>
        <Text size='large' weight='500' textTransform='uppercase'>
          {pluralize(gitIntegrationsLength, 'GIT INTEGRATION')}
        </Text>
        {isSelectedOnlyMode && (
          <FilterChip
            sx={{ marginLeft: '1.5rem' }}
            height='1rem'
            label='Selected repositories'
            deleteIcon={<Icon name='Close' />}
            onDelete={() => selectionProps.setIsSelectedOnly(false)}
          />
        )}
      </Flex>
      <Text size='large' pb={SPACES.medium} pt={SPACES.small}>
        Manage your LinearB git integrations and select the repositories you
        would like LinearB to scan
      </Text>
    </Flex>
  );
};

const AddIntegrationButton = ({
  isTeamLicense,
  teamTooltip,
  onAddIntegration
}) =>
  isTeamLicense ? (
    <Tooltip title={teamTooltip} placement='top-start' interactive>
      <div>
        <LBButton size='lg' disabled data-cy='add-integration'>
          Add Integration
        </LBButton>
      </div>
    </Tooltip>
  ) : (
    <LBButton size='lg' onClick={onAddIntegration} data-cy='add-integration'>
      Add Integration
    </LBButton>
  );

const getFormattedIntegrations = ({
  integrations,
  gitIntegrations,
  account,
  deleteGitIntegrationFF,
  updateSensorAuthorization,
  canAddMoreRepos,
  toggleModal,
  repositories,
  createRepositories,
  setIsProcessingRepos,
  toggleSnackBar,
  getSensorAuthorizations,
  getRepositories,
  deleteSensorAuthorization,
  getTableData,
  tableConfig,
  isTeamLicense,
  showGitlabGitStreamIntegration,
  showGitstreamGitlabServerIntegration,
  showGithubServerGitstreamIntegration,
  showGitstreamBitbucketIntegration,
  autoRepoDiscovery,
  readonly,
  selectionProps,
  fetchRepositoriesExtraParams
}) =>
  orderBy(integrations, 'repos.length', ['desc']).map(
    ({
      id,
      name,
      sensor_name,
      state,
      url,
      repos,
      is_on_prem,
      gitstream_enabled,
      opa_gitstream_installed,
      prStats,
      isMultiTokenEnabled,
      child_sensor_auth_id,
      profile
    }) => (
      <GitIntegration
        profile={profile}
        key={id}
        sensor_name={sensor_name}
        gitIntegrations={gitIntegrations}
        updateSensorAuthorization={updateSensorAuthorization}
        id={id}
        name={name}
        repos={repos}
        state={state}
        getTableData={getTableData}
        url={url}
        deleteGitIntegrationFF={deleteGitIntegrationFF}
        account={account}
        canAddMoreRepos={canAddMoreRepos}
        toggleModal={toggleModal}
        repositories={repositories}
        createRepositories={createRepositories}
        setIsProcessingRepos={setIsProcessingRepos}
        toggleSnackBar={toggleSnackBar}
        isTeamLicense={isTeamLicense}
        getSensorAuthorizations={getSensorAuthorizations}
        getRepositories={getRepositories}
        deleteSensorAuthorization={deleteSensorAuthorization}
        tableConfig={tableConfig({
          readonly,
          showGitStreamColumn:
            (sensor_name.toLowerCase() === PROVIDERS_MAP.gitlab.name &&
              (!url || (url && showGitstreamGitlabServerIntegration)) &&
              showGitlabGitStreamIntegration) ||
            (sensor_name.toLowerCase() ===
              PROVIDERS_MAP.github.name.toLowerCase() &&
              url &&
              showGithubServerGitstreamIntegration) ||
            (showGitstreamBitbucketIntegration &&
              sensor_name ===
                PROVIDERS_MAP.bitbucket.displayName.toLowerCase() &&
              !url),
          sensorAuthId: id,
          isGitStreamHeaderCheckboxEnabled: !!(
            (!is_on_prem && gitstream_enabled) ||
            (is_on_prem && opa_gitstream_installed) ||
            child_sensor_auth_id
          )
        })}
        is_on_prem={is_on_prem}
        showGitStreamIntegration={showGitlabGitStreamIntegration}
        showGitstreamGitlabServerIntegration={
          showGitstreamGitlabServerIntegration
        }
        showGitstreamGithubServerIntegration={
          showGithubServerGitstreamIntegration
        }
        showGitstreamBitbucketIntegration={showGitstreamBitbucketIntegration}
        prStats={prStats}
        autoRepoDiscoveryConfig={autoRepoDiscovery.getSettingByIntegrationId(
          id
        )}
        isMultiTokenEnabled={isMultiTokenEnabled}
        readonly={readonly}
        selectionProps={selectionProps}
        fetchRepositoriesExtraParams={fetchRepositoriesExtraParams}
      />
    )
  );

const GitIntegrations = ({
  gitIntegrations,
  isTeamLicense,
  teamTooltip,
  integrations,
  account,
  deleteGitIntegrationFF,
  updateSensorAuthorization,
  canAddMoreRepos,
  toggleModal,
  repositories,
  createRepositories,
  setIsProcessingRepos,
  toggleSnackBar,
  getSensorAuthorizations,
  getRepositories,
  deleteSensorAuthorization,
  getTableData,
  tableConfig,
  setTerm,
  createHostedModal,
  getModalOptions,
  openPATModal,
  readonly,
  selectionProps,
  fetchRepositoriesExtraParams
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const autoRepoDiscovery = useAutoRepoDiscovery();
  const addIntegrationEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.addIntegrationEnabled
  });
  const showGitlabGitStreamIntegration = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showGitlabGitstreamIntegration
  });
  const showGitstreamGitlabServerIntegration = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showGitstreamGitlabServerIntegration
  });
  const showGithubServerGitstreamIntegration = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.gitstreamGithubServerIntegrationEnabled
  });
  const showGitstreamBitbucketIntegration = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showGitstreamBitbucketIntegration
  });

  const onAddIntegration = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tabs: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
      click_type: TRACKING.CLICK.ADD_INTEGRATION
    });
    const modalOptions = getModalOptions({ toggleModal, isReauth: false });

    const openGitOAuthModal = () =>
      toggleModal(
        createGitOAuthModalOptions(
          {
            onSelectCloudProvider: ({ provider, withPAT = true }) => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.SETTINGS,
                page_tab: TRACKING.PAGE_TABS.NEW_GIT_INTEGRATION,
                click_type: TRACKING.CLICK.SELECT_PROVIDER,
                provider: startCase(provider)
              });
              openPATModal({
                provider,
                withPAT,
                modalOptions,
                toggleModal,
                onBack: openGitOAuthModal
              });
            },
            onSelectSelfHostedProvider: ({ provider }) => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.SETTINGS,
                page_tab: TRACKING.PAGE_TABS.NEW_GIT_INTEGRATION,
                click_type: TRACKING.CLICK.SELECT_PROVIDER,
                provider: startCase(provider.replace('server', '') + ' Server')
              });
              createHostedModal({
                provider,
                onBack: openGitOAuthModal,
                toggleModal,
                modalOptions
              });
            }
          },
          modalOptions
        )
      );

    openGitOAuthModal();
  };

  useEffect(() => {
    (async () => {
      await getSensorAuthorizations({
        withGitStreamEnabled: true,
        add_pr_stats: true
      });
    })();
  }, []);

  return (
    <>
      <Flex justify='space-between' content='center' items='center'>
        <Header
          gitIntegrationsLength={gitIntegrations.length}
          setTerm={setTerm}
          selectionProps={selectionProps}
        />
        <Flex self='flex-start'>
          {addIntegrationEnabledFF && !readonly && (
            <AddIntegrationButton
              isTeamLicense={isTeamLicense}
              teamTooltip={teamTooltip}
              onAddIntegration={onAddIntegration}
            />
          )}
        </Flex>
      </Flex>
      {getFormattedIntegrations({
        integrations,
        account,
        deleteGitIntegrationFF,
        updateSensorAuthorization,
        canAddMoreRepos,
        toggleModal,
        repositories,
        createRepositories,
        setIsProcessingRepos,
        toggleSnackBar,
        getSensorAuthorizations,
        getRepositories,
        deleteSensorAuthorization,
        getTableData,
        tableConfig,
        gitIntegrations,
        isTeamLicense,
        showGitlabGitStreamIntegration,
        showGitstreamGitlabServerIntegration,
        showGithubServerGitstreamIntegration,
        showGitstreamBitbucketIntegration,
        autoRepoDiscovery,
        readonly,
        selectionProps,
        fetchRepositoriesExtraParams
      })}
    </>
  );
};

export default withGitIntegrationsModals(GitIntegrations);
